// src/components/dashboard/DashboardTimeline.tsx
// UPDATED — fixed a real overlap in FILTER_STATUS_MAP: 'negotiating'
// previously appeared in BOTH 'needs-approval' and 'in-progress',
// meaning a single thread could show up in two different sidebar
// sub-filters simultaneously depending on which one you clicked — not a
// crash, but confusing, and not an honest match for what "Needs
// Approval" implies (a decision hasn't been made yet).
//
// Reassigned so each status belongs to exactly one bucket:
//   new         → needs-approval  (nothing decided yet)
//   negotiating → in-progress     (already engaged, actively discussed —
//                                   not stalled waiting on a first decision)
//   committed   → in-progress     (terms locked, execution underway)
//   settled     → completed
//
// Deliberately NOT connected to the real chat-based pendingApproval
// mechanism (see DashboardApprovals.tsx / DashboardHome.tsx) — this
// filter is scoped to counterparty thread status, a structurally
// different concept with no natural join to chat approvals. See prior
// design decision: those two "needs approval" ideas stay separate,
// each honest about what it actually tracks.
//
// MIGRATED — this file previously had its own local FDW_BASE /
// dashboardToken / getDashboardToken / authedFetch block (never
// migrated in the earlier /api prefix pass, since this file wasn't in
// scope for that pass). Now imports the shared client. Call site
// updated: '/dashboard/timeline/threads' → '/timeline/threads' — the
// old path fell through to the SPA catch-all in firebase.json and
// returned index.html instead of JSON, which is what caused the
// "Unexpected token '<'" error on this page.
import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { authedFetch } from '../../services/dashboardApi';

interface MessageThreadSummary {
  threadId: string;
  counterpartyId: string;
  counterpartyLabel: string | null;
  engagementRef: string | null;
  lastMessageAt: string;
  unread: boolean;
  status: 'new' | 'negotiating' | 'committed' | 'verified' | 'settled'; // added
  agreedValueCents: number | null;
}

const STATUS_STYLES: Record<MessageThreadSummary['status'], string> = {
  new: 'bg-brand-surface text-brand-gray',
  negotiating: 'bg-amber-50 text-amber-700',
  committed: 'bg-green-50 text-green-700',
  verified: 'bg-emerald-50 text-emerald-800', // added
  settled: 'bg-blue-50 text-blue-700',
};

const STATUS_LABELS: Record<MessageThreadSummary['status'], string> = {
  new: 'New',
  negotiating: 'Negotiating',
  committed: 'Committed',
  verified: 'Verified', // added
  settled: 'Settled',
};

const FILTER_STATUS_MAP: Record<string, MessageThreadSummary['status'][]> = {
  'needs-approval': ['new'],
  'in-progress': ['negotiating', 'committed'],
  'completed': ['verified', 'settled'], // 'verified' added here, not to in-progress
};

export const DashboardTimeline: React.FC = () => {
  const { filter } = useParams(); // 'needs-approval' | 'in-progress' | 'completed' | undefined
  const [threads, setThreads] = useState<MessageThreadSummary[]>([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await authedFetch('/timeline/threads', { method: 'POST' });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to load message threads.');
        }
        const data = await res.json();
        if (!cancelled) {
          setThreads(data.threads);
          setTotalUnread(data.totalUnread);
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Could not load your timeline.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (isLoading) return <p className="text-sm text-brand-gray font-mono">Loading threads…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  const allowedStatuses = filter ? FILTER_STATUS_MAP[filter] : null;
  const visibleThreads = allowedStatuses
    ? threads.filter((t) => allowedStatuses.includes(t.status))
    : threads;

  const FILTER_LABELS: Record<string, string> = {
    'needs-approval': 'Needs Approval',
    'in-progress': 'In Progress',
    'completed': 'Completed',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">
          {filter ? FILTER_LABELS[filter] ?? 'Timeline' : 'Timeline'}
        </h1>
        {totalUnread > 0 && (
          <span className="text-xs font-mono text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
            {totalUnread} unread
          </span>
        )}
      </div>

      {visibleThreads.length === 0 && (
        <p className="text-sm text-brand-gray">Nothing here yet.</p>
      )}

      <ul className="divide-y divide-brand-border rounded-xl border border-brand-border bg-brand-white">
        {visibleThreads.map((t) => (
          <li key={t.threadId}>
            <Link
              to={`/dashboard/timeline/thread/${encodeURIComponent(t.threadId)}`}
              className="block p-4 hover:bg-brand-surface"
            >
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-brand-black">{t.counterpartyLabel ?? t.counterpartyId}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-mono ${STATUS_STYLES[t.status]}`}>
                    {STATUS_LABELS[t.status]}
                  </span>
                </div>
                {t.unread && <span className="w-2 h-2 rounded-full bg-amber-500 mt-1" />}
              </div>
              {t.agreedValueCents != null && (
                <p className="text-xs font-mono text-brand-charcoal mt-0.5">
                  ${(t.agreedValueCents / 100).toLocaleString()}
                </p>
              )}
              <p className="text-xs text-brand-gray mt-1">{new Date(t.lastMessageAt).toLocaleString()}</p>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};